import { ColumnDef } from "@tanstack/react-table";
import { Avatar } from "@mui/material";
import {
	enumToStringConverter,
	moneyFormat,
} from "../../../helpers/appHelpers";

// Type definitions
interface PayPeriod {
	code: string;
}

interface Employee {
	firstName: string;
	lastName: string;
	photoUrl?: string;
}

interface SavingRow {
	id: number;
	name: string;
	code: string;
	description: string;
	applyEmployeeContributionOn: string;
	applyEmployerContributionOn: string;
	employeeFixedComponent: number;
	employeeRateComponent?: number;
	employerFixedComponent: number;
	employerRateComponent?: number;
	prorate: boolean;
	statutory: boolean;
	startPayPeriod?: PayPeriod;
	endPayPeriod?: PayPeriod;
	createdAt?: string;
	modifiedAt?: string;
}

interface BackPayGroupRow {
	id: number;
	name: string;
	code: string;
	description: string;
	category: string;
	startPayPeriod?: PayPeriod;
	endPayPeriod?: PayPeriod;
	applicablePayPeriod?: PayPeriod;
	createdAt?: string;
	modifiedAt?: string;
}

interface BackPayRow {
	id: number;
	code: string;
	name: string;
	description: string;
	category: string;
	fixedComponent: number | null;
	rateComponent: number | null;
	startPayPeriod?: PayPeriod;
	endPayPeriod?: PayPeriod;
	applicablePayPeriod?: PayPeriod;
	group?: BackPayGroupRow;
	employee?: Employee;
	createdAt?: string;
	modifiedAt?: string;
}

// ========== Savings Columns ==========
export const savingsColumns: ColumnDef<SavingRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "applyEmployeeContributionOn",
		accessorKey: "applyEmployeeContributionOn",
		header: "Apply Employee Contribution On",
		size: 300,
		cell: ({ row }) =>
			enumToStringConverter(row.original.applyEmployeeContributionOn),
	},
	{
		id: "applyEmployerContributionOn",
		accessorKey: "applyEmployerContributionOn",
		header: "Apply Employer Contribution On",
		size: 300,
		cell: ({ row }) =>
			enumToStringConverter(row.original.applyEmployerContributionOn),
	},
	{
		id: "employeeFixedComponent",
		accessorKey: "employeeFixedComponent",
		header: "Employee Fixed Component",
		size: 280,
		cell: ({ row }) => moneyFormat(row.original.employeeFixedComponent),
	},
	{
		id: "employeeRateComponent",
		accessorKey: "employeeRateComponent",
		header: "Employee Rate Component",
		size: 280,
		cell: ({ row }) => {
			const rate = row.original.employeeRateComponent;
			return rate ? (Number(rate) * 100).toFixed(2) + "%" : "--";
		},
	},
	{
		id: "employerFixedComponent",
		accessorKey: "employerFixedComponent",
		header: "Employer Fixed Component",
		size: 280,
		cell: ({ row }) => moneyFormat(row.original.employerFixedComponent),
	},
	{
		id: "employerRateComponent",
		accessorKey: "employerRateComponent",
		header: "Employer Rate Component",
		size: 280,
		cell: ({ row }) => {
			const rate = row.original.employerRateComponent;
			return rate ? (Number(rate) * 100).toFixed(2) + "%" : "--";
		},
	},
	{
		id: "prorate",
		accessorKey: "prorate",
		header: "Prorate",
		size: 140,
		cell: ({ row }) => (row.original.prorate ? "Yes" : "No"),
	},
	{
		id: "statutory",
		accessorKey: "statutory",
		header: "Statutory",
		size: 140,
		cell: ({ row }) => (row.original.statutory ? "Yes" : "No"),
	},
	{
		id: "startPayPeriod",
		accessorKey: "startPayPeriod",
		header: "Start Pay Period",
		size: 220,
		cell: ({ row }) => row.original.startPayPeriod?.code || "--",
	},
	{
		id: "endPayPeriod",
		accessorKey: "endPayPeriod",
		header: "End Pay Period",
		size: 220,
		cell: ({ row }) => row.original.endPayPeriod?.code || "--",
	},
];

export const savingsColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	applyEmployeeContributionOn: true,
	applyEmployerContributionOn: true,
	employeeFixedComponent: false,
	employeeRateComponent: false,
	employerFixedComponent: false,
	employerRateComponent: false,
	prorate: true,
	statutory: true,
	startPayPeriod: false,
	endPayPeriod: false,
};

// ========== Back Pay Groups Columns ==========
export const backPayGroupColumns: ColumnDef<BackPayGroupRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "category",
		accessorKey: "category",
		header: "Category",
		size: 200,
		cell: ({ row }) => enumToStringConverter(row.original.category),
	},
	{
		id: "startPayPeriod",
		accessorKey: "startPayPeriod",
		header: "Start Pay Period",
		size: 220,
		cell: ({ row }) => row.original.startPayPeriod?.code || "--",
	},
	{
		id: "endPayPeriod",
		accessorKey: "endPayPeriod",
		header: "End Pay Period",
		size: 220,
		cell: ({ row }) => row.original.endPayPeriod?.code || "--",
	},
	{
		id: "applicablePayPeriod",
		accessorKey: "applicablePayPeriod",
		header: "Applicable Pay Period",
		size: 240,
		cell: ({ row }) => row.original.applicablePayPeriod?.code || "--",
	},
];

export const backPayGroupColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	category: true,
	startPayPeriod: true,
	endPayPeriod: true,
	applicablePayPeriod: true,
};

// ========== Employee Back Pay Columns ==========
export const backPayColumns: ColumnDef<BackPayRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "category",
		accessorKey: "category",
		header: "Category",
		size: 200,
		cell: ({ row }) => enumToStringConverter(row.original.category),
	},
	{
		id: "fixedComponent",
		accessorKey: "fixedComponent",
		header: "Fixed Component",
		size: 200,
		cell: ({ row }) =>
			row.original.fixedComponent != null
				? moneyFormat(row.original.fixedComponent)
				: "--",
	},
	{
		id: "rateComponent",
		accessorKey: "rateComponent",
		header: "Rate Component",
		size: 200,
		cell: ({ row }) => {
			const rate = row.original.rateComponent;
			return rate != null ? (Number(rate) * 100).toFixed(2) + "%" : "--";
		},
	},
	{
		id: "group",
		accessorKey: "group",
		header: "Back Pay Group",
		size: 220,
		cell: ({ row }) => row.original.group?.name || "--",
	},
	{
		id: "startPayPeriod",
		accessorKey: "startPayPeriod",
		header: "Start Pay Period",
		size: 220,
		cell: ({ row }) => row.original.startPayPeriod?.code || "--",
	},
	{
		id: "endPayPeriod",
		accessorKey: "endPayPeriod",
		header: "End Pay Period",
		size: 220,
		cell: ({ row }) => row.original.endPayPeriod?.code || "--",
	},
	{
		id: "applicablePayPeriod",
		accessorKey: "applicablePayPeriod",
		header: "Applicable Pay Period",
		size: 240,
		cell: ({ row }) => row.original.applicablePayPeriod?.code || "--",
	},
];

export const backPayColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: false,
	category: true,
	fixedComponent: true,
	rateComponent: true,
	group: true,
	startPayPeriod: true,
	endPayPeriod: true,
	applicablePayPeriod: true,
};
